import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { parrainageAPI } from '@/lib/api';
import { useParrainageRefresh } from '@/hooks/useParrainageRefresh';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ParrainageInsights } from '@/components/parrainage/ParrainageInsights';
import { OverrideChainSimulator } from '@/components/admin/OverrideChainSimulator';
import {
    Network, Plus, ChevronDown, ChevronRight, UserPlus, Power, Move, Mail, Trash2,
    Loader2, Settings2, Building2, Users, FileText, Upload, Download, ScrollText, Gauge,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const LEVEL_LABEL = { 1: 'Niveau 1', 2: 'Niveau 2', 3: 'Niveau 3' };
const LEVEL_COLOR = {
    1: 'bg-indigo-100 text-indigo-700',
    2: 'bg-sky-100 text-sky-700',
    3: 'bg-teal-100 text-teal-700',
};

function ParrainNode({ node, depth, onAction, canManage, highlightedId, focusRef }) {
    const [open, setOpen] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const isHighlighted = highlightedId && node.id === highlightedId;
    return (
        <div
            className="select-none"
            data-testid={`parrain-node-${node.id}`}
            ref={isHighlighted ? focusRef : null}
        >
            <div
                className={`flex items-center gap-2 py-2 px-2 rounded-lg border transition-colors ${isHighlighted
                    ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-300 shadow-sm'
                    : 'hover:bg-slate-50 border-transparent hover:border-slate-200'}`}
                style={{ marginLeft: depth * 22 }}
            >
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className={`w-5 h-5 flex items-center justify-center text-slate-400 ${hasChildren ? '' : 'invisible'}`}
                    data-testid={`parrain-toggle-${node.id}`}
                >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <Badge className={`${LEVEL_COLOR[node.parrain_level]} text-xs`}>{LEVEL_LABEL[node.parrain_level]}</Badge>
                <span className="font-medium text-slate-900">{node.first_name} {node.last_name}</span>
                {node.parrain_branch && (
                    <Badge variant="outline" className="text-xs text-slate-500">{node.parrain_branch}</Badge>
                )}
                {!node.is_active && <Badge className="bg-red-100 text-red-700 text-xs">Inactif</Badge>}
                <span className="text-xs text-slate-400 hidden sm:inline">{node.email}</span>
                {canManage && (
                    <div className="ml-auto flex items-center gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" title={node.is_active ? 'Désactiver' : 'Activer'}
                            onClick={() => onAction('toggle', node)} data-testid={`parrain-toggle-status-${node.id}`}>
                            <Power className={`w-4 h-4 ${node.is_active ? 'text-emerald-600' : 'text-red-500'}`} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Déplacer / changer de niveau"
                            onClick={() => onAction('move', node)} data-testid={`parrain-move-${node.id}`}>
                            <Move className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Renvoyer l'invitation"
                            onClick={() => onAction('invite', node)} data-testid={`parrain-invite-${node.id}`}>
                            <Mail className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 hover:bg-red-50" title="Supprimer définitivement"
                            onClick={() => onAction('delete', node)} data-testid={`parrain-delete-${node.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                    </div>
                )}
            </div>
            {hasChildren && open && (
                <div>
                    {node.children.map(child => (
                        <ParrainNode key={child.id} node={child} depth={depth + 1} onAction={onAction} canManage={canManage} highlightedId={highlightedId} focusRef={focusRef} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Parrainage() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    const isParrain = user?.role === 'parrain';
    // Réseau Parrainage : activé par défaut pour tous les VP (plus de switch).
    const vpActivated = true;
    const canManage = isAdmin || isVP;
    const vpNotActivated = false;

    // 🎯 Deep-link Uatu : `/parrainage?parrain=<user_id>` — surligne + scrolle
    // vers le nœud ciblé et ouvre automatiquement le bon réseau VP.
    const [searchParams, setSearchParams] = useSearchParams();
    const focusParrainId = searchParams.get('parrain');
    const focusRef = useRef(null);
    const [highlightedId, setHighlightedId] = useState(null);

    const [vps, setVps] = useState([]);
    const selectedVp = searchParams.get('vp') || (isVP ? user?.id : '');
    const activeTab = searchParams.get('tab') === 'pilotage' ? 'pilotage' : 'reseau';
    const updateNavigation = (key, value) => setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value); else next.delete(key);
        return next;
    }, { replace: true });
    const [tree, setTree] = useState({ roots: [], total: 0 });
    const [flat, setFlat] = useState([]);
    const [commissions, setCommissions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Modal fallback : affiche le lien d'invitation après création/renvoi (au cas où l'email met du temps ou échoue)
    const [inviteInfo, setInviteInfo] = useState(null); // { link, email, name, expires_at }

    // Un admin (même s'il est aussi VP) pilote la vue via le sélecteur de VP.
    // Un VP non-admin reste cantonné à son propre réseau.
    const effectiveVp = isAdmin ? selectedVp : (isVP ? user?.id : selectedVp);
    const networkVpRef = useRef(effectiveVp);
    networkVpRef.current = effectiveVp;
    const networkRequest = useRef(0);
    const commissionRequest = useRef(0);

    const loadVps = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await parrainageAPI.listVps();
            setVps(res.data || []);
        } catch (e) { /* noop */ }
    }, [isAdmin]);

    const loadData = useCallback(async () => {
        const request = ++networkRequest.current;
        if (isAdmin && !effectiveVp) { setTree({ roots: [], total: 0 }); setFlat([]); return; }
        if (isVP && !vpActivated && !isAdmin) { setTree({ roots: [], total: 0 }); setFlat([]); return; }
        setLoading(true);
        try {
            const [t, l] = await Promise.all([
                parrainageAPI.getTree(isParrain ? undefined : effectiveVp),
                parrainageAPI.listParrains(isParrain ? undefined : effectiveVp),
            ]);
            if (request !== networkRequest.current || networkVpRef.current !== effectiveVp) return;
            setTree(t.data || { roots: [], total: 0 });
            setFlat(l.data || []);
        } catch (e) {
            if (request !== networkRequest.current || networkVpRef.current !== effectiveVp) return;
            toast.error(e.response?.data?.detail || 'Erreur de chargement du réseau');
        } finally {
            if (request === networkRequest.current && networkVpRef.current === effectiveVp) setLoading(false);
        }
    }, [isAdmin, isParrain, isVP, vpActivated, effectiveVp]);

    useEffect(() => { loadVps(); }, [loadVps]);
    useEffect(() => {
        setTree({ roots: [], total: 0 }); setFlat([]); setCommissions(null);
        return () => { networkRequest.current += 1; commissionRequest.current += 1; };
    }, [effectiveVp]);

    // 🎯 Deep-link Uatu : `?parrain=<user_id>` — surligne + scrolle vers ce nœud
    // dans l'arbre du VP courant. Pour un admin sans VP encore sélectionné,
    // on attend qu'il choisisse un VP puis on tente le focus.
    useEffect(() => {
        if (!focusParrainId || !flat || flat.length === 0) return;
        const target = flat.find(p => p.id === focusParrainId);
        if (!target) return; // silencieux — l'utilisateur peut changer de VP
        setHighlightedId(focusParrainId);
        setTimeout(() => {
            focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
        const t = setTimeout(() => setHighlightedId(null), 4500);
        return () => clearTimeout(t);
    }, [focusParrainId, flat]);

    // Phase 2b — commissions Parrains (réseau pour admin/VP, ses gains pour le parrain)
    const loadCommissions = useCallback(async () => {
        const request = ++commissionRequest.current;
        if (isAdmin && !effectiveVp) { setCommissions(null); return; }
        if (isVP && !vpActivated && !isAdmin) { setCommissions(null); return; }
        try {
            const res = await parrainageAPI.listCommissions(isAdmin ? effectiveVp : undefined);
            if (request === commissionRequest.current && networkVpRef.current === effectiveVp) setCommissions(res.data);
        } catch {
            if (request === commissionRequest.current && networkVpRef.current === effectiveVp) setCommissions(null);
        }
    }, [isAdmin, isVP, vpActivated, effectiveVp]);
    const refreshNetwork = useCallback(() => { loadData(); loadCommissions(); }, [loadData, loadCommissions]);
    useParrainageRefresh(refreshNetwork, activeTab === 'reseau');

    const handleAction = async (action, node) => {
        if (action === 'toggle') {
            try {
                await parrainageAPI.setStatus(node.id, !node.is_active);
                toast.success(node.is_active ? 'Parrain désactivé' : 'Parrain réactivé');
                loadData();
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        } else if (action === 'invite') {
            try {
                const res = await parrainageAPI.resendInvite(node.id);
                toast.success('Invitation renvoyée');
                const inv = res.data?.invitation;
                if (inv?.reset_link) {
                    setInviteInfo({ link: inv.reset_link, email: node.email, name: `${node.first_name} ${node.last_name}`, expires_at: inv.expires_at });
                }
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        } else if (action === 'move') {
            setMoveTarget(node);
        } else if (action === 'delete') {
            const label = `${node.first_name} ${node.last_name}`;
            const confirm = window.confirm(
                `Supprimer définitivement le parrain "${label}" ?\n\n` +
                `— Cette action est irréversible.\n` +
                `— Les dossiers déjà gagnés et les lignes de commissions historiques restent intacts (nom et taux figés à la création du dossier).\n` +
                `— Un parrain ayant des filleuls ACTIFS ne peut pas être supprimé — déplacez-les ou désactivez-les d'abord.`
            );
            if (!confirm) return;
            try {
                await parrainageAPI.deleteParrain(node.id);
                toast.success('Parrain supprimé');
                loadData();
            } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la suppression'); }
        }
    };

    const counts = { 1: 0, 2: 0, 3: 0 };
    flat.forEach(p => { counts[p.parrain_level] = (counts[p.parrain_level] || 0) + 1; });

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parrainage-page">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <Network className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-outfit font-semibold text-slate-900">Réseau Parrainage</h1>
                            <p className="text-sm text-slate-500">
                                {isParrain ? 'Votre branche et votre réseau' : 'Développez et pilotez votre réseau de parrains'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="parrainage-settings-btn">
                                <Settings2 className="w-4 h-4 mr-2" /> Paramètres
                            </Button>
                        )}
                        {canManage && (
                            <Button
                                onClick={() => setCreateOpen(true)}
                                disabled={isAdmin && !effectiveVp}
                                className="bg-indigo-600 hover:bg-indigo-700"
                                data-testid="parrainage-add-btn"
                            >
                                <UserPlus className="w-4 h-4 mr-2" /> Ajouter un parrain
                            </Button>
                        )}
                    </div>
                </div>

                {vpNotActivated && (
                    <Card className="border-amber-200 bg-amber-50" data-testid="parrainage-not-activated">
                        <CardContent className="p-4 flex flex-wrap items-center gap-3">
                            <Network className="w-5 h-5 text-amber-600" />
                            <p className="text-sm text-amber-800">
                                Le module <strong>Réseau Parrainage</strong> n'est pas activé sur votre compte. Contactez un administrateur pour l'activer.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {isAdmin && (
                    <Card>
                        <CardContent className="p-4 flex flex-wrap items-center gap-3">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <Label className="text-sm text-slate-600">VP propriétaire du réseau :</Label>
                            <Select value={effectiveVp || ''} onValueChange={(value) => updateNavigation('vp', value)}>
                                <SelectTrigger className="w-full sm:w-[280px] min-w-0" data-testid="parrainage-vp-select">
                                    <SelectValue placeholder="Sélectionner un VP (module activé)" />
                                </SelectTrigger>
                                <SelectContent data-testid="parrainage-vp-options">
                                    {vps.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Aucun VP avec module activé</div>}
                                    {vps.map(v => (
                                        <SelectItem key={v.id} value={v.id} data-testid={`parrainage-vp-option-${v.id}`}>{v.first_name} {v.last_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                )}

                {!vpNotActivated && (
                <Tabs value={activeTab} onValueChange={(value) => updateNavigation('tab', value)} className="w-full" data-testid="parrainage-tabs">
                    <TabsList data-testid="parrainage-tablist">
                        <TabsTrigger value="reseau" data-testid="tab-reseau"><Users className="w-4 h-4 mr-1.5" />Réseau</TabsTrigger>
                        <TabsTrigger value="pilotage" data-testid="tab-pilotage"><Gauge className="w-4 h-4 mr-1.5" />Pilotage</TabsTrigger>
                    </TabsList>
                    <TabsContent value="reseau" className="mt-4 space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[1, 2, 3].map(l => (
                        <Card key={l}><CardContent className="p-4">
                            <p className="text-xs text-slate-500">Parrains {LEVEL_LABEL[l]}</p>
                            <p className="text-2xl font-bold text-slate-900">{counts[l] || 0}</p>
                        </CardContent></Card>
                    ))}
                    <Card><CardContent className="p-4">
                        <p className="text-xs text-slate-500">Total Parrains</p>
                        <p className="text-2xl font-bold text-indigo-600">{flat.length}</p>
                    </CardContent></Card>
                </div>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-500" /> Arborescence du réseau
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                        ) : (tree.roots || []).length === 0 ? (
                            <div className="py-12 text-center text-slate-500" data-testid="parrainage-empty">
                                <Network className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                                {isAdmin && !effectiveVp ? 'Sélectionnez un VP pour voir son réseau.' : 'Aucun parrain pour le moment.'}
                            </div>
                        ) : (
                            <div data-testid="parrainage-tree">
                                {tree.roots.map(root => (
                                    <ParrainNode key={root.id} node={root} depth={0} onAction={handleAction} canManage={canManage} highlightedId={highlightedId} focusRef={focusRef} />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Phase 2b — Commissions Parrains */}
                <Card data-testid="parrainage-commissions-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-500" />
                            {isParrain ? 'Mes commissions' : 'Commissions du réseau'}
                            <span className="text-xs font-normal text-slate-400">(validées)</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!commissions || commissions.count === 0 ? (
                            <div className="py-8 text-center text-slate-500 text-sm" data-testid="parrainage-commissions-empty">
                                Aucune commission validée pour le moment.
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-3 gap-3 mb-4">
                                    <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3" data-testid="parrainage-comm-total">
                                        <p className="text-xs text-indigo-700">Total</p>
                                        <p className="text-xl font-bold text-indigo-800">{(commissions.total || 0).toLocaleString('fr-FR')} €</p>
                                    </div>
                                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                                        <p className="text-xs text-slate-500">Courtage</p>
                                        <p className="text-lg font-semibold text-slate-900">{(commissions.by_module?.easy_power || 0).toLocaleString('fr-FR')} €</p>
                                    </div>
                                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                                        <p className="text-xs text-slate-500">Travaux d’optimisation</p>
                                        <p className="text-lg font-semibold text-slate-900">{(commissions.by_module?.chantier || 0).toLocaleString('fr-FR')} €</p>
                                    </div>
                                </div>
                                <div className="space-y-1 max-h-80 overflow-auto">
                                    {commissions.lines.map((l) => (
                                        <div key={l.id} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`parrainage-comm-row-${l.id}`}>
                                            <span className="text-slate-700 truncate mr-2">
                                                <span className="font-medium">{l.prospect_name}</span>
                                                {!isParrain && <span className="text-slate-400"> · N{l.level} {l.parrain_name}</span>}
                                                {/* Un parrain ne voit JAMAIS le taux (%) — uniquement le module et le montant. */}
                                                <span className="text-slate-400"> · {l.module === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}{!isParrain ? ` · ${l.rate}%` : ''}</span>
                                            </span>
                                            <span className="font-semibold text-indigo-800 whitespace-nowrap">{(l.amount || 0).toLocaleString('fr-FR')} €</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
                    </TabsContent>
                    <TabsContent value="pilotage" className="mt-4">
                        <ParrainageInsights
                            role={user?.role}
                            effectiveVp={effectiveVp}
                            isAdmin={isAdmin}
                            isVP={isVP}
                            isParrain={isParrain}
                        />
                    </TabsContent>
                </Tabs>
                )}
            </div>

            {createOpen && (
                <CreateParrainDialog
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    flat={flat}
                    vpId={effectiveVp}
                    isAdmin={isAdmin}
                    onSuccess={(inv) => {
                        setCreateOpen(false);
                        loadData();
                        if (inv?.link) setInviteInfo(inv);
                    }}
                />
            )}
            {moveTarget && (
                <MoveParrainDialog
                    node={moveTarget}
                    flat={flat}
                    isAdmin={isAdmin}
                    onOpenChange={(o) => !o && setMoveTarget(null)}
                    onSuccess={() => { setMoveTarget(null); loadData(); }}
                />
            )}
            {settingsOpen && (
                <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
            )}
            {/* Modal fallback : lien d'invitation copiable (utile si l'email met du temps
                à arriver, atterrit en spam, ou si l'admin veut le transmettre autrement) */}
            <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
                <DialogContent className="max-w-lg" data-testid="invite-link-dialog">
                    <DialogHeader>
                        <DialogTitle>Invitation créée pour {inviteInfo?.name}</DialogTitle>
                        <DialogDescription>
                            Un email a été planifié vers <b>{inviteInfo?.email}</b>. Vous pouvez aussi transmettre le lien manuellement (utile si l'email tarde ou termine en spam).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                            <div className="text-xs text-slate-500 mb-1">Lien de création de mot de passe (valide 7 jours) :</div>
                            <div className="flex items-center gap-2">
                                <Input readOnly value={inviteInfo?.link || ''} className="text-xs font-mono" data-testid="invite-link-input" onFocus={(e) => e.target.select()} />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { navigator.clipboard?.writeText(inviteInfo?.link || ''); toast.success('Lien copié !'); }}
                                    data-testid="invite-link-copy"
                                >
                                    Copier
                                </Button>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500">
                            Astuce : si le parrain ne reçoit rien d'ici quelques minutes, copiez ce lien et transmettez-le par SMS ou WhatsApp.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setInviteInfo(null)} data-testid="invite-link-close">Fermer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}

function CreateParrainDialog({ open, onOpenChange, flat, vpId, isAdmin, onSuccess }) {
    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', parrain_level: '1', parrain_branch: '', parrain_parent_id: '', commission_percent_ep_override: '', commission_percent_ch_override: '', password: '', confirm_password: '' });
    const [saving, setSaving] = useState(false);
    const [globalRates, setGlobalRates] = useState({ ep: null, ch: null });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const level = parseInt(form.parrain_level, 10);
    const parentCandidates = flat.filter(p => p.parrain_level === level - 1 && p.is_active);

    // Récupère les taux globaux "direct" pour les afficher en placeholder / info
    useEffect(() => {
        if (!open) return;
        parrainageAPI.getSettings().then(res => {
            const d = res.data || {};
            setGlobalRates({ ep: d.easy_power?.direct ?? null, ch: d.chantier?.direct ?? null });
        }).catch(() => {});
    }, [open]);

    const submit = async () => {
        if (!form.first_name || !form.last_name || !form.email) { toast.error('Nom, prénom et email requis'); return; }
        if (level === 1 && !form.parrain_branch.trim()) { toast.error('Nom de branche requis pour un Niveau 1'); return; }
        if (level > 1 && !form.parrain_parent_id) { toast.error('Parrain de rattachement requis'); return; }
        if (!form.password || form.password.length < 6) { toast.error('Mot de passe requis (6 caractères minimum)'); return; }
        if (form.password !== form.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
        setSaving(true);
        try {
            const epOv = form.commission_percent_ep_override.trim();
            const chOv = form.commission_percent_ch_override.trim();
            await parrainageAPI.createParrain({
                first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone,
                parrain_level: level,
                parrain_branch: level === 1 ? form.parrain_branch.trim() : undefined,
                parrain_parent_id: level > 1 ? form.parrain_parent_id : undefined,
                parrain_vp_id: isAdmin ? vpId : undefined,
                commission_percent_ep_override: epOv === '' ? null : Number(epOv),
                commission_percent_ch_override: chOv === '' ? null : Number(chOv),
                password: form.password,
            });
            toast.success('Parrain créé — identifiants envoyés par e-mail');
            onSuccess(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="create-parrain-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Nouveau parrain</DialogTitle>
                    <DialogDescription>Renseignez ses identifiants — il recevra un e-mail avec son login et son mot de passe, prêts à l'emploi.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Prénom</Label><Input value={form.first_name} onChange={e => set('first_name', e.target.value)} data-testid="parrain-first-name" /></div>
                        <div><Label>Nom</Label><Input value={form.last_name} onChange={e => set('last_name', e.target.value)} data-testid="parrain-last-name" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="parrain-email" /></div>
                        <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="parrain-phone" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Niveau</Label>
                            <Select value={form.parrain_level} onValueChange={v => { set('parrain_level', v); set('parrain_parent_id', ''); }}>
                                <SelectTrigger data-testid="parrain-level-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">Niveau 1</SelectItem>
                                    <SelectItem value="2">Niveau 2</SelectItem>
                                    <SelectItem value="3">Niveau 3</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {level === 1 ? (
                            <div><Label>Nom de branche</Label><Input value={form.parrain_branch} onChange={e => set('parrain_branch', e.target.value)} placeholder="Ex : Secteur Nord" data-testid="parrain-branch" /></div>
                        ) : (
                            <div>
                                <Label>Rattaché à (Niveau {level - 1})</Label>
                                <Select value={form.parrain_parent_id} onValueChange={v => set('parrain_parent_id', v)}>
                                    <SelectTrigger data-testid="parrain-parent-select"><SelectValue placeholder="Choisir le parrain" /></SelectTrigger>
                                    <SelectContent>
                                        {parentCandidates.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Aucun parrain de Niveau {level - 1}</div>}
                                        {parentCandidates.map(p => (
                                            <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.parrain_branch}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Mot de passe *</Label>
                            <Input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="6 caractères minimum" data-testid="parrain-password" />
                        </div>
                        <div>
                            <Label>Confirmation *</Label>
                            <Input type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Retapez le mot de passe" data-testid="parrain-confirm-password" />
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500 -mt-2">Le parrain recevra un e-mail contenant son identifiant ({form.email || 'son adresse e-mail'}) et ce mot de passe.</p>
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2" data-testid="parrain-commission-override-block">
                        <div className="text-xs font-semibold text-indigo-800">Taux personnalisé (optionnel)</div>
                        {isAdmin ? (
                            <>
                                <p className="text-[11px] text-slate-500 leading-tight">
                                    {form.parrain_level === '1'
                                        ? "Override N1 = budget TOTAL sur toute la branche. Le restant après distribution aux niveaux en dessous lui revient. Doit être ≥ 14% Courtage et ≥ 8% Travaux d’optimisation."
                                        : "Un N2/N3 utilise toujours les taux globaux — pas de personnalisation possible."}
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">% Courtage</Label>
                                        <Input
                                            type="number" step="0.1" min="14" max="100"
                                            disabled={form.parrain_level !== '1'}
                                            value={form.commission_percent_ep_override}
                                            onChange={e => set('commission_percent_ep_override', e.target.value)}
                                            placeholder={form.parrain_level === '1' ? '≥ 14 (vide = taux global)' : 'N1 uniquement'}
                                            data-testid="parrain-override-ep"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">% Travaux d’optimisation</Label>
                                        <Input
                                            type="number" step="0.1" min="8" max="100"
                                            disabled={form.parrain_level !== '1'}
                                            value={form.commission_percent_ch_override}
                                            onChange={e => set('commission_percent_ch_override', e.target.value)}
                                            placeholder={form.parrain_level === '1' ? '≥ 8 (vide = taux global)' : 'N1 uniquement'}
                                            data-testid="parrain-override-ch"
                                        />
                                    </div>
                                </div>
                                {form.parrain_level === '1' && (
                                    <OverrideChainSimulator
                                        epOverride={form.commission_percent_ep_override}
                                        chOverride={form.commission_percent_ch_override}
                                    />
                                )}
                            </>
                        ) : (
                            <p className="text-[11px] text-slate-600 leading-relaxed" data-testid="parrain-override-vp-notice">
                                Ce parrain sera créé aux <b>taux globaux du réseau</b>. Si vous souhaitez lui donner plus, demandez à un administrateur de personnaliser ses taux.
                            </p>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="parrain-create-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer & envoyer les identifiants'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MoveParrainDialog({ node, flat, isAdmin, onOpenChange, onSuccess }) {
    const [level, setLevel] = useState(String(node.parrain_level));
    const [parentId, setParentId] = useState(node.parrain_parent_id || '');
    const [branch, setBranch] = useState(node.parrain_branch || '');
    const [epOv, setEpOv] = useState(node.commission_percent_ep_override != null ? String(node.commission_percent_ep_override) : '');
    const [chOv, setChOv] = useState(node.commission_percent_ch_override != null ? String(node.commission_percent_ch_override) : '');
    const [globalRates, setGlobalRates] = useState({ ep: null, ch: null });
    const [saving, setSaving] = useState(false);
    const lvl = parseInt(level, 10);
    const parentCandidates = flat.filter(p => p.parrain_level === lvl - 1 && p.is_active && p.id !== node.id);

    useEffect(() => {
        parrainageAPI.getSettings().then(res => {
            const d = res.data || {};
            setGlobalRates({ ep: d.easy_power?.direct ?? null, ch: d.chantier?.direct ?? null });
        }).catch(() => {});
    }, []);

    const submit = async () => {
        if (lvl === 1 && !branch.trim()) { toast.error('Nom de branche requis'); return; }
        if (lvl > 1 && !parentId) { toast.error('Parrain de rattachement requis'); return; }
        setSaving(true);
        try {
            // 1) Move (niveau/branche/rattachement)
            const structuralChanged = lvl !== node.parrain_level
                || (lvl === 1 && branch.trim() !== (node.parrain_branch || ''))
                || (lvl > 1 && parentId !== (node.parrain_parent_id || ''));
            if (structuralChanged) {
                await parrainageAPI.moveParrain(node.id, {
                    parrain_level: lvl,
                    parrain_parent_id: lvl > 1 ? parentId : undefined,
                    parrain_branch: lvl === 1 ? branch.trim() : undefined,
                });
            }
            // 2) Overrides commission — envoyer avec flag reset si vidés. Réservé aux Admins.
            const initEp = node.commission_percent_ep_override;
            const initCh = node.commission_percent_ch_override;
            const nowEp = epOv.trim();
            const nowCh = chOv.trim();
            const payload = {};
            if (isAdmin) {
                if (nowEp === '' && initEp != null) payload.reset_ep_override = true;
                else if (nowEp !== '' && Number(nowEp) !== initEp) payload.commission_percent_ep_override = Number(nowEp);
                if (nowCh === '' && initCh != null) payload.reset_ch_override = true;
                else if (nowCh !== '' && Number(nowCh) !== initCh) payload.commission_percent_ch_override = Number(nowCh);
            }
            if (Object.keys(payload).length > 0) {
                await parrainageAPI.updateParrain(node.id, payload);
            }
            toast.success('Modifications enregistrées (les dossiers existants restent figés)');
            onSuccess();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={true} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="move-parrain-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Move className="w-5 h-5" /> Déplacer {node.first_name} {node.last_name}</DialogTitle>
                    <DialogDescription>Les changements n'affectent que les futurs dossiers (les dossiers existants restent figés).</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label>Niveau</Label>
                        <Select value={level} onValueChange={v => { setLevel(v); setParentId(''); }}>
                            <SelectTrigger data-testid="move-level-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Niveau 1</SelectItem>
                                <SelectItem value="2">Niveau 2</SelectItem>
                                <SelectItem value="3">Niveau 3</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {lvl === 1 ? (
                        <div><Label>Nom de branche</Label><Input value={branch} onChange={e => setBranch(e.target.value)} data-testid="move-branch" /></div>
                    ) : (
                        <div>
                            <Label>Rattaché à (Niveau {lvl - 1})</Label>
                            <Select value={parentId} onValueChange={setParentId}>
                                <SelectTrigger data-testid="move-parent-select"><SelectValue placeholder="Choisir le parrain" /></SelectTrigger>
                                <SelectContent>
                                    {parentCandidates.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.parrain_branch}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2" data-testid="parrain-edit-override-block">
                        <div className="text-xs font-semibold text-indigo-800">Taux personnalisé (position « direct »)</div>
                        {isAdmin ? (
                            <>
                                <p className="text-[11px] text-slate-500 leading-tight">
                                    Vide = utilise le taux global. Ne change pas les dossiers existants.
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">% Courtage</Label>
                                        <Input type="number" step="0.1" min="0" max="100" value={epOv}
                                            onChange={e => setEpOv(e.target.value)}
                                            placeholder={globalRates.ep != null ? `Défaut ${globalRates.ep}%` : 'ex: 8'}
                                            data-testid="parrain-edit-override-ep" />
                                    </div>
                                    <div>
                                        <Label className="text-xs">% Travaux d’optimisation</Label>
                                        <Input type="number" step="0.1" min="0" max="100" value={chOv}
                                            onChange={e => setChOv(e.target.value)}
                                            placeholder={globalRates.ch != null ? `Défaut ${globalRates.ch}%` : 'ex: 5'}
                                            data-testid="parrain-edit-override-ch" />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="text-[11px] text-slate-600 leading-relaxed" data-testid="parrain-edit-override-vp-notice">
                                Ce parrain est rémunéré aux <b>taux globaux du réseau</b>. Si vous souhaitez lui donner plus, demandez à un administrateur de modifier ses taux.
                            </p>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} data-testid="move-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SettingsDialog({ open, onOpenChange }) {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [reglement, setReglement] = useState('');
    const [uploadingTpl, setUploadingTpl] = useState(false);
    const tplRef = useRef(null);
    useEffect(() => {
        parrainageAPI.getSettings().then(r => { setS(r.data); setReglement(r.data?.reglement || ''); }).catch(() => toast.error('Erreur chargement paramètres'));
    }, []);
    const setRate = (type, key, val) => setS(prev => ({ ...prev, [type]: { ...prev[type], [key]: val } }));
    const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v));
    const save = async () => {
        setSaving(true);
        try {
            await parrainageAPI.updateSettings({
                easy_power: { direct: num(s.easy_power.direct), n1: num(s.easy_power.n1), n2: num(s.easy_power.n2) },
                chantier: { direct: num(s.chantier.direct), n1: num(s.chantier.n1), n2: num(s.chantier.n2) },
                reglement,
            });
            toast.success('Paramètres enregistrés');
            onOpenChange(false);
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };
    const onTpl = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingTpl(true);
        try {
            const fd = new FormData(); fd.append('file', file);
            const r = await parrainageAPI.uploadTemplate(fd);
            setS(prev => ({ ...prev, contract_template: r.data }));
            toast.success('Modèle de contrat mis en ligne');
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
        finally { setUploadingTpl(false); if (tplRef.current) tplRef.current.value = ''; }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="parrainage-settings-dialog">
                <DialogHeader>
                    <DialogTitle>Paramètres Réseau Parrainage</DialogTitle>
                    <DialogDescription>Paramètres globaux (Admin uniquement). Taux % prélevés sur la commission VP.</DialogDescription>
                </DialogHeader>
                {!s ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
                    <div className="space-y-6 py-2">
                        <div>
                            <p className="font-medium text-sm mb-3">Taux de commission Parrains</p>
                            {['easy_power', 'chantier'].map(type => (
                                <div key={type} className="mb-3">
                                    <p className="text-sm text-slate-600 mb-2">{type === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[['direct', 'Direct'], ['n1', 'N+1'], ['n2', 'N+2']].map(([k, lbl]) => (
                                            <div key={k}>
                                                <Label className="text-xs">{lbl} (%)</Label>
                                                <Input type="number" step="0.1" value={s[type]?.[k] ?? ''} onChange={e => setRate(type, k, e.target.value)} data-testid={`rate-${type}-${k}`} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div>
                            <p className="font-medium text-sm mb-2 flex items-center gap-2"><ScrollText className="w-4 h-4" /> Règlement du parrainage</p>
                            <p className="text-xs text-slate-500 mb-2">Affiché à tous les parrains dans leur espace. HTML simple accepté.</p>
                            <Textarea rows={6} value={reglement} onChange={e => setReglement(e.target.value)} placeholder="Saisissez le règlement / les règles du programme de parrainage..." data-testid="parrainage-reglement-input" />
                        </div>

                        <div>
                            <p className="font-medium text-sm mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Modèle de contrat (global)</p>
                            <p className="text-xs text-slate-500 mb-2">Déposez le contrat qui sera proposé au téléchargement à TOUS les parrains.</p>
                            <input ref={tplRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={onTpl} data-testid="parrainage-template-input" />
                            <div className="flex items-center gap-3">
                                <Button variant="outline" onClick={() => tplRef.current?.click()} disabled={uploadingTpl} data-testid="parrainage-template-upload-btn">
                                    {uploadingTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                    {s.contract_template ? 'Remplacer le modèle' : 'Déposer le modèle'}
                                </Button>
                                {s.contract_template && <span className="text-xs text-emerald-700 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{s.contract_template.filename}</span>}
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={save} disabled={saving || !s} data-testid="settings-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
